import { useState } from 'react';

import { calculateProfit, CREDIT_CARD_FEE_RATE, CREDIT_CARD_SHARE, exampleProfitInputs, SIMPLE_TAX_RATE, type ProfitInputs } from '../lib/profitSimulator';

type NumberField = Exclude<keyof ProfitInputs, 'guestMode'>;
type Draft = Record<NumberField, string> & { guestMode: ProfitInputs['guestMode'] };

const numberFields: NumberField[] = [
  'unitPrice', 'dailyGuests', 'businessDays', 'laborCost', 'foodCostRate', 'rent',
  'otherFixedCost', 'seats', 'occupancyRate', 'rotations',
];
const inputLimits: Record<NumberField, number> = {
  unitPrice: 1_000_000,
  dailyGuests: 100_000,
  businessDays: 31,
  laborCost: 1_000_000_000,
  foodCostRate: 100,
  rent: 1_000_000_000,
  otherFixedCost: 1_000_000_000,
  seats: 100_000,
  occupancyRate: 100,
  rotations: 100,
};

const toDraft = (inputs: ProfitInputs): Draft => {
  const draft = { guestMode: inputs.guestMode } as Draft;
  for (const key of numberFields) draft[key] = String(inputs[key]);
  return draft;
};

const fromDraft = (draft: Draft): ProfitInputs => {
  const inputs = { guestMode: draft.guestMode } as ProfitInputs;
  for (const key of numberFields) {
    const value = Number(draft[key]);
    inputs[key] = Number.isFinite(value) ? Math.min(inputLimits[key], Math.max(0, value)) : 0;
  }
  inputs.businessDays = Math.round(inputs.businessDays);
  inputs.seats = Math.round(inputs.seats);
  return inputs;
};

const yen = (value: number): string => {
  const amount = `¥${Math.round(Math.abs(value)).toLocaleString('ja-JP')}`;
  return value < 0 ? `▲${amount}` : amount;
};

const people = (value: number): string => Number.isInteger(value)
  ? value.toLocaleString('ja-JP')
  : value.toLocaleString('ja-JP', { maximumFractionDigits: 1 });

const percent = (value: number | null): string => value === null ? '—' : `${value.toFixed(1)}%`;

interface SliderFieldProps {
  id: NumberField;
  label: string;
  unit: string;
  value: string;
  max: number;
  step: number;
  hint?: string;
  onChange: (value: string) => void;
}

const SliderField = ({ id, label, unit, value, max, step, hint, onChange }: SliderFieldProps) => {
  const parsedValue = Number(value);
  const numericValue = Number.isFinite(parsedValue) ? Math.max(0, parsedValue) : 0;
  const hardMax = inputLimits[id];
  const sliderMax = Math.min(hardMax, Math.max(max, Math.ceil(numericValue / step) * step));
  return <div className="profit-field">
    <div className="profit-field-heading">
      <label htmlFor={`profit-${id}`}>{label}</label>
      <div className="profit-field-number">
        <input
          id={`profit-${id}`}
          type="number"
          inputMode="decimal"
          min="0"
          max={hardMax}
          step={step}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onBlur={() => {
            const clamped = Math.min(hardMax, numericValue);
            onChange(String(id === 'businessDays' || id === 'seats' ? Math.round(clamped) : clamped));
          }}
        />
        <span>{unit}</span>
      </div>
    </div>
    <input
      className="profit-field-range"
      type="range"
      aria-label={`${label}をスライダーで調整`}
      min="0"
      max={sliderMax}
      step={step}
      value={Math.min(sliderMax, Math.max(0, numericValue))}
      onChange={(event) => onChange(event.target.value)}
    />
    {hint && <p>{hint}</p>}
  </div>;
};

const Metric = ({ label, value, note, emphasis = false }: { label: string; value: string; note?: string; emphasis?: boolean }) => (
  <div className={`profit-metric${emphasis ? ' profit-metric-emphasis' : ''}`}>
    <span>{label}</span>
    <strong>{value}</strong>
    {note && <small>{note}</small>}
  </div>
);

export const ProfitSimulatorPage = () => {
  const [draft, setDraft] = useState<Draft>(() => toDraft(exampleProfitInputs));

  const inputs = fromDraft(draft);
  const result = calculateProfit(inputs);
  const updateField = (key: NumberField, value: string) => setDraft((previous) => ({ ...previous, [key]: value }));
  const restore = (scenario: ProfitInputs) => setDraft(toDraft(scenario));

  return <div className="profit-page">
    <header className="profit-heading">
      <span>KPI LAB / PROFIT SIMULATOR</span>
      <h1>利益シミュレーター</h1>
      <p>客単価・来客数・費用を動かすと、カード手数料と簡易消費税を含む月間残額がその場で変わります。</p>
    </header>

    <div className="profit-independent-heading"><span>FREE SIMULATION / MONTHLY P&amp;L</span><h2>客単価・来客数から自由に試算</h2><p>カード決済は売上の60％、手数料はカード売上の4％と仮定します。</p></div>

    <div className="profit-notice">
      <strong>サンプル値から開始</strong>
      <span>初期値はご提示の計算例です。実績・給与マスタは取得せず、入力内容はサーバーへ保存・送信されません。</span>
    </div>

    <section className="profit-hero" aria-live="polite">
      <div>
        <span>MONTHLY BALANCE / 手数料・簡易消費税を引いた月間残額</span>
        <strong className={result.monthlyBalance < 0 ? 'profit-hero-negative' : ''}>{yen(result.monthlyBalance)}</strong>
        <p>売上に対する残額 {percent(result.balanceMargin)} <i aria-hidden="true">/</i> 月間来客数 {people(result.monthlyGuests)}人</p>
      </div>
      <div className="profit-hero-status">{result.monthlyBalance >= 0 ? 'プラス' : 'マイナス'}</div>
    </section>

    <div className="profit-metrics">
      <Metric label="月商" value={yen(result.sales)} note="客単価 × 月間来客数" />
      <Metric label="粗利" value={yen(result.grossProfit)} note="売上 − 食材原価" />
      <Metric label="固定費" value={yen(result.fixedCost)} note="人件費 ＋ 家賃 ＋ その他" />
      <Metric label="残額が0円になる月商" value={result.breakEvenSales === null ? '算出不可' : yen(result.breakEvenSales)} note="カード手数料・簡易消費税を反映" emphasis />
    </div>

    <div className="profit-workspace">
      <section className="profit-input-panel">
        <div className="profit-section-heading">
          <div><span>01 / CONDITIONS</span><h2>条件を動かす</h2></div>
          <button type="button" onClick={() => restore(exampleProfitInputs)}>サンプルに戻す</button>
        </div>
        <p className="profit-section-intro">数値を直接入力するか、スライダーで調整できます。</p>
        <h3>売上の前提</h3>
        <SliderField id="unitPrice" label="客単価" unit="円" value={draft.unitPrice} max={10_000} step={100} onChange={(value) => updateField('unitPrice', value)} />
        <div className="profit-mode-toggle" role="group" aria-label="来客数の計算方法">
          <button type="button" className={draft.guestMode === 'direct' ? 'selected' : ''} aria-pressed={draft.guestMode === 'direct'} onClick={() => setDraft((previous) => ({ ...previous, guestMode: 'direct' }))}>来客数を直接入力</button>
          <button type="button" className={draft.guestMode === 'seats' ? 'selected' : ''} aria-pressed={draft.guestMode === 'seats'} onClick={() => setDraft((previous) => ({ ...previous, guestMode: 'seats' }))}>座席から試算</button>
        </div>
        {draft.guestMode === 'direct' ? (
          <SliderField id="dailyGuests" label="1日来客数" unit="人" value={draft.dailyGuests} max={100} step={1} onChange={(value) => updateField('dailyGuests', value)} />
        ) : <div className="profit-seat-inputs">
          <SliderField id="seats" label="座席数" unit="席" value={draft.seats} max={100} step={1} onChange={(value) => updateField('seats', value)} />
          <SliderField id="occupancyRate" label="1回転あたり平均着席率" unit="%" value={draft.occupancyRate} max={100} step={1} onChange={(value) => updateField('occupancyRate', value)} />
          <SliderField id="rotations" label="1日の回転数" unit="回" value={draft.rotations} max={5} step={0.1} onChange={(value) => updateField('rotations', value)} />
          <p>座席数 × 平均着席率 × 回転数 ＝ 1日 {people(result.dailyGuests)}人（試算）</p>
        </div>}
        <SliderField id="businessDays" label="月間営業日数" unit="日" value={draft.businessDays} max={31} step={1} onChange={(value) => updateField('businessDays', value)} />
        <h3>費用の前提</h3>
        <SliderField id="foodCostRate" label="食材原価率" unit="%" value={draft.foodCostRate} max={70} step={0.5} onChange={(value) => updateField('foodCostRate', value)} />
        <SliderField id="laborCost" label="月間人件費" unit="円" value={draft.laborCost} max={3_000_000} step={10_000} onChange={(value) => updateField('laborCost', value)} />
        <SliderField id="rent" label="月間家賃" unit="円" value={draft.rent} max={1_000_000} step={10_000} onChange={(value) => updateField('rent', value)} />
        <SliderField id="otherFixedCost" label="その他月間固定費" unit="円" value={draft.otherFixedCost} max={1_500_000} step={10_000} hint="初期50万円はご提示の例示値で、実績ではありません。水道光熱費・通信費などを入力してください。" onChange={(value) => updateField('otherFixedCost', value)} />
      </section>

      <div className="profit-results">
        <section className="profit-break-even">
          <div className="profit-section-heading"><div><span>02 / BREAK EVEN</span><h2>黒字化まで</h2></div></div>
          {result.monthlyBalance >= 0 ? <p className="profit-achieved">手数料と簡易消費税を引いても、月間残額はプラスです。</p> : result.breakEvenSales === null ? <p className="profit-unavailable">この原価率では、売上を増やしても固定費・カード手数料を回収できません。原価率を下げてください。</p> : <>
            <p className="profit-gap">必要な追加売上 <strong>{yen(result.salesGap ?? 0)}</strong></p>
            <div className="profit-levers">
              <div><span>客単価を変えないなら</span><strong>{result.additionalWholeGuests === null ? '算出不可' : `1日 +${people(result.additionalWholeGuests)}人`}</strong><small>{result.additionalWholeGuests === null ? '客単価と営業日数を入力してください' : `整数の来客数で黒字化する最小増分（計算値 +${result.additionalDailyGuests?.toFixed(2)}人）`}</small></div>
              <div><span>来客数を変えないなら</span><strong>{result.requiredUnitPrice === null ? '算出不可' : `${yen(result.requiredUnitPrice)} / 人`}</strong><small>{result.requiredUnitPrice === null ? '来客数か営業日数を入力してください' : `現在より ${yen(Math.max(0, result.requiredUnitPrice - inputs.unitPrice))} の値上げ`}</small></div>
              <div><span>売上を変えないなら</span><strong>固定費 −{yen(result.fixedCostReduction)}</strong><small>月間残額を0円にするための削減額</small></div>
            </div>
          </>}
        </section>

        <section className="profit-pl">
          <div className="profit-section-heading"><div><span>03 / MONTHLY P&amp;L</span><h2>月次損益の内訳</h2></div></div>
          <dl>
            <div><dt>月間来客数 <small>{people(result.dailyGuests)}人 × {people(inputs.businessDays)}日</small></dt><dd>{people(result.monthlyGuests)}人</dd></div>
            <div><dt>売上</dt><dd>{yen(result.sales)}</dd></div>
            <div><dt>食材原価 <small>{percent(inputs.foodCostRate)}</small></dt><dd>−{yen(result.foodCost)}</dd></div>
            <div className="profit-pl-subtotal"><dt>粗利</dt><dd>{yen(result.grossProfit)}</dd></div>
            <div><dt>人件費</dt><dd>−{yen(inputs.laborCost)}</dd></div>
            <div><dt>家賃</dt><dd>−{yen(inputs.rent)}</dd></div>
            <div><dt>その他固定費</dt><dd>−{yen(inputs.otherFixedCost)}</dd></div>
            <div><dt>カード決済手数料 <small>カード売上小計 × {percent(CREDIT_CARD_FEE_RATE * 100)}</small></dt><dd>−{yen(result.cardFee)}</dd></div>
            <div className="profit-pl-subtotal"><dt>営業利益の概算 <small>簡易消費税を引く前</small></dt><dd>{yen(result.operatingProfit)}</dd></div>
            <div><dt>簡易消費税 <small>（売上 − 食材原価）× {percent(SIMPLE_TAX_RATE * 100)}</small></dt><dd>−{yen(result.estimatedConsumptionTax)}</dd></div>
            <div className="profit-pl-total"><dt>月間残額 <small>売上に対して {percent(result.balanceMargin)}</small></dt><dd className={result.monthlyBalance < 0 ? 'profit-negative' : 'profit-positive'}>{yen(result.monthlyBalance)}</dd></div>
          </dl>
          <div className="profit-card-breakdown">
            <h3>カード決済分の小計</h3>
            <p>月商の{percent(CREDIT_CARD_SHARE * 100)}をカード決済、カード売上の{percent(CREDIT_CARD_FEE_RATE * 100)}を手数料と仮定します。</p>
            <dl>
              <div><dt>カード売上小計</dt><dd>{yen(result.cardSales)}</dd></div>
              <div><dt>カード手数料</dt><dd>−{yen(result.cardFee)}</dd></div>
              <div className="profit-card-net"><dt>カード入金見込</dt><dd>{yen(result.cardNetReceipts)}</dd></div>
            </dl>
          </div>
        </section>
      </div>
    </div>

    <p className="profit-footnote">この画面は入力条件による試算です。消費税はご指定の簡易式で計算しており、実際の納税額やカード会社ごとの手数料率とは異なります。借入返済・減価償却などは含めません。</p>
    <div className="profit-mobile-live" aria-live="polite"><span>月間残額</span><strong>{yen(result.monthlyBalance)}</strong><span>売上比 {percent(result.balanceMargin)}</span></div>
  </div>;
};
